import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { OrderIdParams } from '@src/core/entities';
import { DroneEntity, DroneStatusType, useDroneProvider } from '@src/core/features';
import { setMapCenter } from '@src/core/controllers';
import { routesConfig } from '@src/core/routes';

import { useCancelOrderMutation, useGetOrderQuery } from '@src/features/order/redux';
import {
  selectDrone,
  setHoveredDrone,
  setSelectedDrone,
  setSidebarType,
} from '@src/features/order/pages/upsert-mission-page/redux';
import { SidebarType } from '@src/features/order/pages/upsert-mission-page/entities';

import { UseSelectDroneReturn } from './types';

export const useSelectDrone = (): UseSelectDroneReturn => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const params = useParams<OrderIdParams>();

  // Selectors
  const selected = useSelector(selectDrone);

  // Queries & Mutations
  const { drones, isLoading: isDroneLoading } = useDroneProvider(DroneStatusType.IDLE);
  const { isLoading: isOrderLoading } = useGetOrderQuery(params.orderId);
  const [cancelOrder, { isLoading: isCanceling }] = useCancelOrderMutation();

  // Derived values
  const orderId = params.orderId;
  const isStepByStep = queryParams.get('step-by-step');
  const droneSerial = queryParams.get('droneSerial');
  const isLoading = isDroneLoading || isOrderLoading;
  const hasDrones = drones.length > 0;

  // Auto-select drone from URL params
  useEffect(() => {
    if (droneSerial && drones.length > 0 && !selected) {
      const droneFromParams = drones.find((d) => d.serialNumber === droneSerial);
      if (droneFromParams) {
        dispatch(setSelectedDrone(droneFromParams));
        dispatch(setSidebarType(SidebarType.ENTER_ROUTE_FORM));
      }
    }
  }, [droneSerial, drones, selected, dispatch]);

  // Handlers
  const onSelectDrone = useCallback(
    (drone: DroneEntity) => {
      if (drone.id === selected?.id) {
        dispatch(setSelectedDrone(null));
        return;
      }

      const lat = Number(drone.latitude);
      const lng = Number(drone.longitude);

      if (isNaN(lat) || isNaN(lng)) return;

      dispatch(setSelectedDrone(drone));
      dispatch(setMapCenter({ lat, lng }));
    },
    [selected, dispatch]
  );

  const goForth = useCallback(() => {
    if (!selected) {
      toast.error('Please select a drone');
      return;
    }
    dispatch(setSidebarType(SidebarType.ENTER_ROUTE_FORM));
  }, [selected, dispatch]);

  const goBack = useCallback(() => {
    const path = '/app/orders/update';
    if (isStepByStep) {
      navigate(`${path}/${orderId}?step-by-step=${isStepByStep}`, { replace: true });
      return;
    }
    navigate(-1);
  }, [isStepByStep, orderId, navigate]);

  const onCancel = useCallback(() => {
    cancelOrder(orderId)
      .unwrap()
      .then(() => {
        toast.success('Order has been canceled');
        navigate(routesConfig.orders, { replace: true });
      })
      .catch(() => {});
  }, [orderId, cancelOrder, navigate]);

  const onMouseEnter = useCallback(
    (drone: DroneEntity) => {
      dispatch(setHoveredDrone(drone));
    },
    [dispatch]
  );

  const onMouseLeave = useCallback(() => {
    dispatch(setHoveredDrone(null));
  }, [dispatch]);

  const onExit = useCallback(() => {
    navigate(routesConfig.orders);
  }, [navigate]);

  return {
    // State
    drones,
    selected,
    isLoading,
    isCanceling,
    hasDrones,

    // Handlers
    onSelectDrone,
    goForth,
    goBack,
    onCancel,
    onExit,
    onMouseEnter,
    onMouseLeave,
  };
};
